import { Controller } from "@hotwired/stimulus"

const SCRIPT_ID = "ze-snippet"
const SCRIPT_URL = "https://static.zdassets.com/ekr/snippet.js?key=5338ee83-5778-4a38-a3c2-4731ffdbdab1"
let zendeskLoading

function loadZendesk() {
  if (typeof globalThis.zE === "function") return Promise.resolve(globalThis.zE)
  if (zendeskLoading) return zendeskLoading

  zendeskLoading = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID)
    const script = existingScript || document.createElement("script")

    if (!existingScript) {
      script.id = SCRIPT_ID
      script.async = true
      script.src = SCRIPT_URL
    }

    script.addEventListener("load", () => resolve(globalThis.zE), { once: true })
    script.addEventListener("error", reject, { once: true })
    if (!existingScript) document.head.append(script)
  }).catch(error => {
    zendeskLoading = null
    document.getElementById(SCRIPT_ID)?.remove()
    throw error
  })

  return zendeskLoading
}

export default class extends Controller {
  static targets = ["button", "buttonLabel", "error", "loadingIcon", "readyIcon"]

  static values = {
    email: String,
    jwtUrl: String,
    locale: String,
    name: String,
    openingText: String,
    readyText: String,
    timeout: { type: Number, default: 10000 }
  }

  initialize() {
    this.eventsRegistered = false
    this.opening = false
    this.openingCommandSent = false
    this.widgetLoaded = false
    this.handleConnected = this.handleConnected.bind(this)
    this.handleOpen = this.handleOpen.bind(this)
  }

  connect() {
    if (!this.hasButtonTarget) return

    this.configureSettings()
    this.showReady()
    this.startPreparationTimeout()
    this.prepare()
  }

  disconnect() {
    this.clearTimeouts()
  }

  open() {
    this.showOpening()
    this.startOpeningTimeout()

    this.prepare().then(zendesk => {
      if (!zendesk) this.showError()
      else this.openWidget()
    })
  }

  afterNextPaint() {
    return new Promise(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })
  }

  retry() {
    this.open()
  }

  prepare() {
    this.ready ||= loadZendesk()
      .then(zendesk => {
        if (typeof zendesk !== "function") return null

        this.widgetLoaded = true
        zendesk("webWidget", "hide")
        zendesk("webWidget", "setLocale", this.localeValue)
        zendesk("webWidget", "identify", { name: this.nameValue, email: this.emailValue })
        this.registerEvents(zendesk)
        return zendesk
      })
      .catch(error => {
        this.ready = null
        console.error("Unable to load Zendesk", error)
        this.showError()
        return null
      })

    return this.ready
  }

  registerEvents(zendesk) {
    if (this.eventsRegistered) return

    this.eventsRegistered = true
    zendesk("webWidget:on", "chat:connected", this.handleConnected)
    zendesk("webWidget:on", "open", this.handleOpen)

    if (zendesk("webWidget:get", "chat:departments") !== undefined) this.handleConnected()
  }

  handleConnected() {
    clearTimeout(this.preparationTimeout)
    this.preparationTimeout = null
    if (this.opening) this.openWidget()
    else this.showReady()
  }

  handleOpen() {
    this.checkWidgetVisibility()
  }

  showReady() {
    this.hideError()
    this.setButtonState(this.readyTextValue, { loading: false, disabled: false })
  }

  showOpening() {
    this.opening = true
    this.openingCommandSent = false
    clearTimeout(this.preparationTimeout)
    this.preparationTimeout = null
    this.hideError()
    this.setButtonState(this.openingTextValue, { loading: true, disabled: true })
  }

  showError() {
    this.opening = false
    this.openingCommandSent = false
    this.clearTimeouts()
    this.buttonTarget.hidden = true
    this.errorTarget.hidden = false
  }

  hideError() {
    this.errorTarget.hidden = true
    this.buttonTarget.hidden = false
  }

  setButtonState(label, { loading, disabled }) {
    this.buttonLabelTarget.textContent = label
    this.buttonTarget.disabled = disabled
    if (loading) this.buttonTarget.setAttribute("aria-busy", "true")
    else this.buttonTarget.removeAttribute("aria-busy")
    this.loadingIconTarget.hidden = !loading
    this.readyIconTarget.hidden = loading
  }

  startPreparationTimeout() {
    this.preparationTimeout = setTimeout(() => {
      if (this.widgetLoaded) this.showReady()
      else this.showError()
    }, this.timeoutValue)
  }

  startOpeningTimeout() {
    clearTimeout(this.openingTimeout)
    this.openingTimeout = setTimeout(() => this.showError(), this.timeoutValue)
  }

  openWidget() {
    if (this.openingCommandSent) return

    this.openingCommandSent = true
    this.afterNextPaint().then(() => {
      if (!this.opening) return

      const zendesk = globalThis.zE
      if (typeof zendesk !== "function") {
        this.showError()
        return
      }

      zendesk("webWidget", "show")
      zendesk("webWidget", "open")
      this.checkWidgetVisibility()
    })
  }

  checkWidgetVisibility() {
    if (!this.opening || this.visibilityCheckTimeout) return

    const widget = document.getElementById("webWidget")
    const bounds = widget?.getBoundingClientRect()
    const visible = bounds?.width > 0 && bounds?.height > 0 && bounds.right > 0 && bounds.bottom > 0 &&
      bounds.left < globalThis.innerWidth && bounds.top < globalThis.innerHeight

    if (visible) {
      this.finishOpening()
      return
    }

    this.visibilityCheckTimeout = setTimeout(() => {
      this.visibilityCheckTimeout = null
      this.checkWidgetVisibility()
    }, 100)
  }

  finishOpening() {
    this.opening = false
    this.openingCommandSent = false
    clearTimeout(this.openingTimeout)
    clearTimeout(this.visibilityCheckTimeout)
    this.openingTimeout = null
    this.visibilityCheckTimeout = null
    this.showReady()
  }

  clearTimeouts() {
    clearTimeout(this.preparationTimeout)
    clearTimeout(this.openingTimeout)
    clearTimeout(this.visibilityCheckTimeout)
    this.preparationTimeout = null
    this.openingTimeout = null
    this.visibilityCheckTimeout = null
  }

  configureSettings() {
    const jwtUrl = this.jwtUrlValue

    globalThis.zESettings = {
      analytics: false,
      webWidget: {
        position: { horizontal: "right", vertical: "bottom" },
        color: {
          theme: "#EC008C",
          launcher: "#EC008C",
          launcherText: "#FFFFFF",
          button: "#D90082",
          header: "#D90082"
        },
        chat: {
          connectOnPageLoad: true
        },
        authenticate: {
          chat: {
            jwtFn: callback => {
              fetch(jwtUrl)
                .then(response => {
                  if (!response.ok) throw new Error(`Zendesk JWT request failed (${response.status})`)

                  return response.text()
                })
                .then(token => {
                  if (!token) throw new Error("Zendesk JWT response is empty")

                  callback(token)
                })
                .catch(error => console.error("Unable to authenticate Zendesk", error))
            }
          }
        }
      }
    }
  }
}
